import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { ReadyState } from 'react-use-websocket';

/**
 * Stream type constants for categorizing WebSocket streams.
 */
export const STREAM_TYPES = {
  ALARMS: 'alarms',
  OTHER: 'other',
  READINGS: 'readings',
  SETTINGS: 'settings',
  SHUTDOWN_BANNER: 'shutdownBanner',
  TRENDS: 'trends',
  USER_ACTIVITY: 'userActivity',
} as const;

export type StreamType = (typeof STREAM_TYPES)[keyof typeof STREAM_TYPES];

/**
 * Immutable stream metadata (separate from dynamic state).
 */
export interface StreamMetadata {
  readonly pathname: string;
  readonly streamType: StreamType;
  readonly displayName: string;
}

/**
 * Stream connection status for a single WebSocket stream.
 *
 * `readyState` is the single source of truth for connection state, synced directly
 * from the useWebSocket hook. Derive connection booleans from it:
 * - connected: readyState === ReadyState.OPEN
 * - reconnecting: readyState === ReadyState.CONNECTING
 */
export interface StreamStatus {
  pathname: string;
  readyState: ReadyState; // synced from useWebSocket — source of truth for connection state
  isPendingReconnect: boolean; // true when disconnected and waiting for backoff before next attempt
  circuitBreakerTripped: boolean;
  reconnectHistory: number[]; // timestamps of reconnect attempts
  lastDisconnectAt: number | null; // timestamp of last disconnect
  lastCloseEvent: CloseEvent | null; // most recent WebSocket close event
  reconnectAttemptNumber: number; // current reconnect attempt (0-indexed)
  keepaliveStreak: number;
  lastKeepaliveAt: number | null;
  unstableCloseCount: number;
}

/**
 * Atom family for stream metadata (immutable after initialization).
 */
export const streamMetadataFamily = atomFamily(
  (pathname: string) =>
    atom<StreamMetadata>({
      pathname,
      streamType: STREAM_TYPES.OTHER,
      displayName: pathname.split('/').pop() || pathname,
    }),
  (a: string, b: string) => a === b
);

/**
 * Atom family keyed by WebSocket pathname.
 * Each stream gets its own atom instance to track connection status.
 */
export const streamStatusFamily = atomFamily(
  (pathname: string) =>
    atom<StreamStatus>({
      pathname,
      readyState: ReadyState.UNINSTANTIATED,
      isPendingReconnect: false,
      circuitBreakerTripped: false,
      reconnectHistory: [],
      lastDisconnectAt: null,
      lastCloseEvent: null,
      reconnectAttemptNumber: 0,
      keepaliveStreak: 0,
      lastKeepaliveAt: null,
      unstableCloseCount: 0,
    }),
  (a: string, b: string) => a === b
);

/**
 * Tracks which stream pathnames are currently active (mounted).
 */
export const activeStreamsAtom = atom<string[]>([]);
activeStreamsAtom.debugLabel = 'StreamStatus.activeStreams';

/**
 * Write-only atom to add an active stream pathname.
 */
export const addActiveStreamAtom = atom(null, (get, set, pathname: string) => {
  const current = get(activeStreamsAtom);
  if (!current.includes(pathname)) {
    set(activeStreamsAtom, [...current, pathname]);
  }
});

/**
 * Write-only atom to remove an active stream pathname.
 */
export const removeActiveStreamAtom = atom(null, (get, set, pathname: string) => {
  const current = get(activeStreamsAtom);
  set(
    activeStreamsAtom,
    current.filter((p) => p !== pathname)
  );
});

/**
 * Derived atom: true if any active stream has a tripped circuit breaker.
 */
export const anyCircuitBreakerTrippedAtom = atom((get) => {
  const activeStreams = get(activeStreamsAtom);
  return activeStreams.some((pathname) => {
    const status = get(streamStatusFamily(pathname));
    return status.circuitBreakerTripped;
  });
});
anyCircuitBreakerTrippedAtom.debugLabel = 'StreamStatus.anyCircuitBreakerTripped';
